import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import styled from 'styled-components';

const ParticleContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  opacity: 0.3;
`;

function Particles() {
  const mesh = useRef();
  const light = useRef();
  
  // Create particles
  const particles = useMemo(() => {
    const temp = [];
    for (let i = 0; i < 200; i++) {
      temp.push({
        position: [
          (Math.random() - 0.5) * 100,
          (Math.random() - 0.5) * 100,
          (Math.random() - 0.5) * 100
        ],
        speed: Math.random() * 0.01 + 0.001
      });
    }
    return temp;
  }, []);

  const particlePositions = useMemo(() => {
    const positions = new Float32Array(particles.length * 3);
    particles.forEach((particle, i) => {
      positions[i * 3] = particle.position[0];
      positions[i * 3 + 1] = particle.position[1];
      positions[i * 3 + 2] = particle.position[2];
    });
    return positions;
  }, [particles]);

  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    if (mesh.current) {
      // Rotate particle system
      mesh.current.rotation.y = time * 0.05;
      mesh.current.rotation.x = time * 0.02;
      
      // Update particle positions
      const positions = mesh.current.geometry.attributes.position.array;
      particles.forEach((particle, i) => {
        // Floating motion
        positions[i * 3 + 1] += Math.sin(time + particle.position[0]) * particle.speed;
        
        // Boundary check and reset
        if (positions[i * 3 + 1] > 50) {
          positions[i * 3 + 1] = -50;
        }
      });
      mesh.current.geometry.attributes.position.needsUpdate = true;
    }
    
    if (light.current) {
      light.current.position.x = Math.cos(time * 0.5) * 50;
      light.current.position.z = Math.sin(time * 0.5) * 50;
    }
  });

  return (
    <>
      <ambientLight intensity={0.1} />
      <pointLight
        ref={light}
        position={[10, 10, 10]}
        intensity={0.8}
        color="#3b82f6"
        distance={100}
      />
      
      <points ref={mesh}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={particles.length}
            array={particlePositions}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.5}
          color="#06b6d4"
          transparent
          opacity={0.6}
          sizeAttenuation={true}
        />
      </points>
    </>
  );
}

function ParticleBackground() {
  return (
    <ParticleContainer>
      <Canvas
        camera={{ position: [0, 0, 30], fov: 75 }}
        style={{ background: 'transparent' }}
      >
        <Particles />
      </Canvas>
    </ParticleContainer>
  );
}

export default ParticleBackground;